import type { Agent } from "@/types/agent/agent";
import type { WidgetTemplateId, WidgetSize } from "@/types/ui/widget-styles";
import { getWidgetTemplate } from "@/data/widget-templates";
import { DEFAULT_BRANDING, WidgetCaptionStyle, WidgetPosition } from "@/types/ui/branding";
import { BUTTON_SIZE_MAP, WIDGET_SIZE_PRESETS } from "@/types/ui/widget-styles";

export const useEmbedCode = (
  genie: Agent,
  templateId: WidgetTemplateId = "classic",
  popupSizeOverride?: WidgetSize,
  positionOverride?: WidgetPosition,
  buttonLabelOverride?: string | null,
  captionOverride?: WidgetCaptionStyle | null,
  mode: "conversation" | "email" | "chat" = "conversation",
) => {
  const baseUrl = import.meta.env.VITE_APP_URL || window.location.origin;
  const primaryColor = genie.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const urlName = genie.url_name ?? genie.page?.url_name ?? genie.name.toLowerCase().replace(/\s+/g, "-");
  const avatarUrl = genie.branding?.logoUrl || genie.branding?.customAvatarUrl || "";
  const position = positionOverride ?? "bottom-right";

  const template = getWidgetTemplate(templateId);
  const config = template?.config;

  // Generate button color based on theme
  const getButtonColor = () => {
    if (!config) return "#dfeaf4";
    if (config.theme.style === "dark") return "#374151";
    if (config.theme.glassEffect) return "rgba(255,255,255,0.7)";
    return `${primaryColor}15`;
  };

  // Every template now serves the same LiveAgent interior in the popup.
  // The iframe-direct embed code uses /live-agent/?embed=true for all variants.
  const embedPath = `/${mode === "email" ? "email-genie" : "live-agent"}/${encodeURIComponent(urlName)}?embed=true${mode === "chat" ? "&mode=chat" : ""}`;
  const scriptString = (value: string) => JSON.stringify(value).replace(/</g, "\\u003c");

  // Get size values from config, with optional override from the size selector
  const buttonSize = config ? BUTTON_SIZE_MAP[config.button.size] : 64;
  const resolvedPopupSize = popupSizeOverride ?? config?.popup.size ?? "medium";
  const popupSize = WIDGET_SIZE_PRESETS[resolvedPopupSize] ?? WIDGET_SIZE_PRESETS.medium;

  // Generate template-specific configuration object
  const generateConfigObject = () => {
    if (!config) return "";

    const configLines = [
      `    agentUrl: ${scriptString(urlName)},`,
      ...(mode !== "conversation" ? [`    mode: '${mode}',`] : []),
      `    template: '${templateId}',`,
      `    position: '${position}',`,
      `    primaryColor: ${scriptString(primaryColor)},`,
      `    buttonColor: ${scriptString(getButtonColor())},`,
      `    baseUrl: ${scriptString(baseUrl)},`,
      `    agentName: ${scriptString(genie.name)},`,
    ];

    // Add avatar if present
    if (avatarUrl) {
      configLines.push(`    customAvatarUrl: ${scriptString(avatarUrl)},`);
    }

    // Add button configuration
    configLines.push(`    buttonShape: '${config.button.shape}',`);
    configLines.push(`    buttonSize: ${buttonSize},`);

    const buttonLabel = buttonLabelOverride ?? (config.button.showLabel ? config.button.labelText : undefined);
    if (buttonLabel) {
      configLines.push(`    buttonLabel: ${scriptString(buttonLabel)},`);
    }

    configLines.push(`    showOnlineIndicator: ${config.button.showOnlineIndicator},`);

    // Add popup configuration
    configLines.push(`    popupSize: '${resolvedPopupSize}',`);
    configLines.push(`    popupAnimation: '${config.popup.animation}',`);
    configLines.push(`    popupBorderRadius: ${config.popup.borderRadius},`);
    configLines.push(`    shadowIntensity: '${config.popup.shadowIntensity}',`);

    // Add theme configuration
    configLines.push(`    theme: '${config.theme.style}',`);
    configLines.push(`    glassEffect: ${config.theme.glassEffect},`);

    // Add caption bubble configuration
    if (captionOverride?.enabled && captionOverride.text) {
      configLines.push(`    captionEnabled: true,`);
      configLines.push(`    captionText: '${captionOverride.text.replace(/'/g, "\\'")}',`);
      configLines.push(`    captionSize: '${captionOverride.size ?? "medium"}',`);
      configLines.push(`    captionBackgroundColor: '${captionOverride.backgroundColor ?? "#ffffff"}',`);
      configLines.push(`    captionTextColor: '${captionOverride.textColor ?? "#1f2937"}',`);
      configLines.push(`    captionDelay: ${captionOverride.delayMs ?? 4000},`);
      configLines.push(`    captionAutoHide: ${captionOverride.autoHideMs ?? 8000}`);
    }

    return configLines.join("\n");
  };

  /* Magic template loads the v2 runtime (different file, different namespace,
     different DOM). Two scripts coexist on the CDN — customers' sites only
     download the one their snippet references. This lets us roll v2 out
     opt-in per customer without flipping anyone automatically. */
  const isMagicV2 = templateId === "magic-v2" && mode === "conversation";

  /* Magic embed snippet — pass only the customer's primaryColor and let
     widget-v2.js derive a brand-appropriate accent (hue-shift +35° in HSL)
     so each customer's gradient is on-brand, not Help Genie's blue→pink. */
  const magicV2ScriptEmbedCode = `<!-- HelpGenie Widget - Magic Style (v2) -->
<script src="${baseUrl}/widget-v2.js" async></script>
<script>
  window.addEventListener('load', function () {
    if (!window.HelpGenieV2) return;
    HelpGenieV2.init({
      agentUrl: '${urlName}',
      baseUrl: '${baseUrl}',
      primaryColor: '${primaryColor}',
      position: '${position}',
      title: '${genie.name}',
      subtitle: '${(buttonLabelOverride ?? "Voice or text — your call").replace(/'/g, "\\'")}'${
        avatarUrl
          ? `,
      avatarIconUrl: '${avatarUrl}'`
          : ""
      }${
        captionOverride?.enabled && captionOverride.text
          ? `,
      peekoutEnabled: true,
      peekoutText: '${captionOverride.text.replace(/'/g, "\\'")}',
      peekoutSize: '${captionOverride.size ?? "medium"}',
      peekoutBackgroundColor: '${captionOverride.backgroundColor ?? "#ffffff"}',
      peekoutTextColor: '${captionOverride.textColor ?? "#1f2937"}',
      peekoutDelay: ${captionOverride.delayMs ?? 6000},
      peekoutAutoHide: ${captionOverride.autoHideMs ?? 7000}`
          : `,
      peekoutEnabled: false`
      }
    });
  });
</script>
<!-- End HelpGenie Widget -->`;

  const classicScriptEmbedCode = `<!-- HelpGenie Widget - ${template?.name || "Classic"} Style -->
<script>
  (function(w,d,s,o,f,js,fjs){
    w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments)};
    w[o].q=w[o].q||[];
    js=d.createElement(s),fjs=d.getElementsByTagName(s)[0];
    js.id=o;js.src=f;js.async=1;fjs.parentNode.insertBefore(js,fjs);
  }(window,document,'script','hg','${baseUrl}/widget.js'));

  hg('init', {
${generateConfigObject()}
  });
</script>
<!-- End HelpGenie Widget -->`;

  const scriptEmbedCode = isMagicV2 ? magicV2ScriptEmbedCode : classicScriptEmbedCode;

  // For iframe embed, use appropriate dimensions based on template
  const iframeWidth = templateId === "immersive" ? 500 : templateId === "magic-v2" ? 460 : popupSize.width;
  const iframeHeight = templateId === "immersive" ? 700 : templateId === "magic-v2" ? 620 : popupSize.height;
  const iframeBorderRadius = config?.popup.borderRadius || 8;

  const iframeEmbedCode = `<iframe
  src="${baseUrl}${embedPath}"
  width="${iframeWidth}"
  height="${iframeHeight}"
  frameborder="0"
  ${mode === "email" ? 'title="Ask your Genie by email"' : mode === "chat" ? 'title="Chat with your Genie"' : 'allow="microphone"'}
  style="border: none; border-radius: ${iframeBorderRadius}px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);"
></iframe>`;

  const iframeReactCode = `<iframe
  src="${baseUrl}${embedPath}"
  width="${iframeWidth}"
  height="${iframeHeight}"
  frameBorder="0"
  ${mode === "email" ? 'title="Ask your Genie by email"' : mode === "chat" ? 'title="Chat with your Genie"' : 'allow="microphone"'}
  style={{border: 'none', borderRadius: '${iframeBorderRadius}px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)'}}
/>`;

  const fullPagePath = `/live-agent/${urlName}`;

  const fullPageHtmlCode = `<iframe
  src="${baseUrl}${fullPagePath}"
  width="100%"
  height="100%"
  frameborder="0"
  allow="microphone"
  style="border: none; display: block;"
></iframe>`;

  const fullPageReactCode = `<iframe
  src="${baseUrl}${fullPagePath}"
  width="100%"
  height="100%"
  frameBorder="0"
  allow="microphone"
  style={{ border: 'none', display: 'block' }}
/>`;

  return {
    scriptEmbedCode,
    iframeEmbedCode,
    iframeReactCode,
    fullPageHtmlCode,
    fullPageReactCode,
    primaryColor,
    urlName,
    baseUrl,
    templateId,
  };
};
